import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Divider, FormControlLabel, Link, Stack, Switch, Typography } from '@mui/material';
import AuthLayout from './AuthLayout';
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { useUserService } from '../../services/user.service';
import { useNotificationService } from '../../services/notificationService';
import { formatFirebaseAuthError } from '../../utils/errorHandling';
import { SignupFormData, signupSchema } from '../../utils/validation/auth.schema';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import FormTextField from './FormTextField';
import { hideCelloReferralWidget } from '../../utils/domUtils';

const SignupPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { signup, loginWithGoogle } = useAuth();
    const [errorKey, setErrorKey] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const userService = useUserService();
    const notificationService = useNotificationService();

    const {
        control,
        handleSubmit: formSubmit,
        watch,
        formState: { isSubmitting, isValid, isDirty, touchedFields },
        trigger,
    } = useForm<SignupFormData>({
        resolver: zodResolver(signupSchema),
        defaultValues: {
            email: '',
            password: '',
            marketingEmails: false,
        },
        mode: 'onBlur',
    });

    // Watch the password for strength indicator
    const password = watch('password');
    const email = watch('email');

    // Check if form is valid for submit button
    const isFormValid = isValid && isDirty && email && password;

    // Only trigger validation after user has interacted with fields
    useEffect(() => {
        // Only validate if user has already interacted with the field
        if (touchedFields.email || touchedFields.password) {
            trigger();
        }
    }, [email, password, trigger, touchedFields]);

    const handleSubmit = async (data: SignupFormData) => {
        try {
            setErrorKey(null);
            setLoading(true);
            const user = await signup(data.email, data.password);
            userService.log('Sign up');

            if (user) {
                hideCelloReferralWidget();
                // Save marketing email preference using notification service
                if (user.uid) {
                    try {
                        await notificationService.updateNotifications({
                            productUpdates: data.marketingEmails
                        });
                    } catch (err) {
                        console.error('Failed to save notification settings:', err);
                    }
                }

                // Check if user is new based on creation vs last sign-in time
                if (user.metadata.creationTime === user.metadata.lastSignInTime) {
                    // New user - redirect to onboarding regardless of email verification status
                    navigate('/onboarding');
                } else {
                    // Existing user trying to sign up again - redirect to dashboard
                    navigate('/dashboard');
                }
            }
        } catch (err: any) {
            setErrorKey(formatFirebaseAuthError(err));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        try {
            setErrorKey(null);
            setLoading(true);
            
            // Store the information needed after redirect
            sessionStorage.setItem('googleAuthIntent', 'signup');
            
            // This will redirect to Google
            await loginWithGoogle();
            
            // Code below won't execute immediately due to redirect
        } catch (err: any) {
            setErrorKey(formatFirebaseAuthError(err));
            console.error(err);
            setLoading(false);
        }
    };

    const subtitleElement = (
        <Box sx={{ fontSize: '0.95rem' }}>{t('auth.signup.hasAccount')} <Link 
            component={RouterLink} 
            to="/login" 
            sx={{ 
                color: 'text.primary', 
                textDecoration: 'underline', 
                '&:hover': { 
                    color: 'text.secondary' 
                } 
            }}
            aria-label={t('auth.signup.loginLinkAriaLabel')}
        >
            {t('auth.signup.loginLink')}
        </Link></Box>
    );

    return (
        <AuthLayout 
            title={t('auth.signup.title')}
            subtitle={subtitleElement}
        >
                
                {errorKey && (
                    <Alert
                        severity="error"
                        sx={{
                            marginBottom: '16px',
                            borderRadius: 1,
                        }}
                        role="alert"
                        aria-live="assertive"
                    >
                        {t(errorKey)}
                    </Alert>
                )}

            <Box
                component="form"
                onSubmit={formSubmit(handleSubmit)}
                sx={{
                    width: '100%',
                    marginTop: '8px',
                }}
                noValidate
            >
                <Stack spacing={1.5}>
                    <FormTextField
                        required
                        fullWidth
                        label={t('auth.email')}
                        type="email"
                        autoComplete="email"
                        name="email"
                        control={control}
                        inputProps={{
                            'aria-label': t('auth.email'),
                        }}
                        disabled={isSubmitting || loading}
                    />

                    <FormTextField
                        required
                        fullWidth
                        label={t('auth.password')}
                        autoComplete="new-password"
                        name="password"
                        control={control}
                        showPasswordToggle
                        inputProps={{
                            'aria-label': t('auth.password'),
                        }}
                        disabled={isSubmitting || loading}
                    />

                    {/* Password strength indicator */}
                    {password && <PasswordStrengthIndicator password={password} />}

                    {/* Marketing Email Preference */}
                    <Box sx={{ mt: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Controller
                            name="marketingEmails"
                            control={control}
                            render={({ field }) => (
                                <>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                {...field}
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ fontSize: '0.9rem', color: 'text.primary', fontWeight: 500 }}>
                                                {t('settings.notifications.productUpdates')}
                                            </Typography>
                                        }
                                        sx={{ 
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    />
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            display: 'block', 
                                            textAlign: 'center',
                                            mt: 0.5, 
                                            color: 'text.secondary',
                                            fontSize: '0.75rem'
                                        }}
                                    >
                                        {t('settings.notifications.noSpam')}
                                    </Typography>
                                </>
                            )}
                        />
                    </Box>

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={isSubmitting || loading || !isFormValid}
                        sx={{
                            marginTop: '12px',
                            marginBottom: '16px', // Added/Adjusted marginBottom
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '1rem',
                            height: '48px',
                            borderRadius: 1,
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 'none',
                            },
                        }}
                        aria-label={t('auth.signup.createAccountAriaLabel')}
                    >
                        {t('auth.signup.submitButton')}
                    </Button>
                </Stack>
            </Box>

            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                    margin: '20px 0',
                    width: '100%',
                }}
            >
                <Divider
                    sx={{
                        flexGrow: 1,
                        backgroundColor: 'divider',
                        height: '1.5px',
                    }}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', px: 2, fontSize: '0.85rem', opacity: 0.6 }}>
                    {t('auth.signup.or')}
                </Typography>
                <Divider
                    sx={{
                        flexGrow: 1,
                        backgroundColor: 'divider',
                        height: '1.5px',
                    }}
                />
            </Stack>

            <Button
                fullWidth
                variant="outlined"
                onClick={handleGoogleSignIn}
                disabled={isSubmitting || loading}
                sx={{
                    width: '100%',
                    height: '48px',
                    borderWidth: '1.5px',
                    color: 'text.primary',
                    backgroundColor: 'background.paper',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '1rem',
                    marginBottom: '8px',
                    borderRadius: 1,
                    '&:hover': {
                        backgroundColor: 'action.hover',
                        borderColor: 'primary.light',
                    },
                }}
                aria-label={t('auth.signup.signupWithGoogleAriaLabel')}
                startIcon={
                    <Box
                        component="span"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '8px',
                        }}
                    >
                        <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                            <path
                                fill="#EA4335"
                                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                            />
                            <path
                                fill="#4285F4"
                                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                            />
                            <path
                                fill="#FBBC05"
                                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                            />
                            <path
                                fill="#34A853"
                                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                            />
                        </svg>
                    </Box>
                }
            >
                {t('auth.signup.signupWithGoogle')}
            </Button>

            {/* Terms and conditions moved to bottom */}
            <Typography
                variant="body2"
                sx={{
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                    textAlign: 'center',
                    mt: 3,
                    mb: 1,
                }}
            >
                {t('auth.signup.termsText')}{' '}
                <Link
                    href="https://www.coinfox.io/de/agb"
                    target="_blank"
                    aria-label={t('auth.signup.termsLinkAriaLabel')}
                    sx={{
                        color: 'text.primary',
                        textDecoration: 'underline',
                        fontWeight: 400,
                        '&:hover': {
                            color: 'text.secondary',
                        },
                    }}
                >
                    {t('auth.signup.termsLink')}
                </Link>{' '}
                {t('auth.signup.and')}{' '}
                <Link
                    href="https://www.coinfox.io/de/datenschutz"
                    target="_blank"
                    aria-label={t('auth.signup.privacyLinkAriaLabel')}
                    sx={{
                        color: 'text.primary',
                        textDecoration: 'underline',
                        fontWeight: 400,
                        '&:hover': {
                            color: 'text.secondary',
                        },
                    }}
                >
                    {t('auth.signup.privacyLink')}
                </Link>.
            </Typography>

            </AuthLayout>
    );
};

export default SignupPage;
